import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import AuthPage from './pages/AuthPage';
import AdminLogin from './pages/AdminLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import DayDetailPage from './pages/DayDetailPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import QuizPage from './pages/QuizPage';
import LandingPage from './pages/LandingPage';
import EnrollmentPage from './pages/EnrollmentPage';
import AdminEnrollmentsPage from './pages/AdminEnrollmentsPage';
import AdminEnrollmentDetailPage from './pages/AdminEnrollmentDetailPage';
import CourseAccessPage from './pages/CourseAccessPage';
import AdminCurriculumProposalsPage from './pages/AdminCurriculumProposalsPage';
import Background from './components/Background';
import ChatBot from './components/ChatBot';
import './App.css';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const configuredApi = process.env.REACT_APP_BACKEND_URL?.trim();
  const API = (configuredApi || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api')).replace(/\/$/, '');

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    const restoreSession = async () => {
      if (!storedToken || !storedUser) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });

        if (response.ok) {
          const currentUser = await response.json();
          setToken(storedToken);
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } else if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        } else {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, [API]);

  useEffect(() => {
    if (!token) {
      setUser(null);
    }
  }, [token]);

  const login = (newToken, newUser) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    window.location.replace('/');
  };

  const updateUser = (updatedUser) => {
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  return (
    <ThemeProvider>
      <AuthContext.Provider value={{ token, user, login, logout, updateUser, API }}>
        <Background />
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <ChatBot />
          {isLoading ? (
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-lg">Loading...</div>
            </div>
          ) : (
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={!token ? <AuthPage /> : user?.role === 'admin' ? <Navigate to="/admin" replace /> : <Navigate to="/dashboard" replace />} />
              <Route path="/admin-login" element={!token ? <AdminLogin /> : user?.role === 'admin' ? <Navigate to="/admin" replace /> : <Navigate to="/" replace />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/dashboard" element={token && (user?.role === 'intern' || user?.role === 'admin') ? <Dashboard /> : <Navigate to="/" replace />} />
              <Route path="/dashboard/day/:dayNumber" element={token && (user?.role === 'intern' || user?.role === 'admin') ? <DayDetailPage /> : <Navigate to="/" replace />} />
              <Route path="/admin" element={token && user?.role === 'admin' ? <AdminDashboard /> : <Navigate to="/" replace />} />
              <Route path="/admin/users/:userId" element={token && user?.role === 'admin' ? <AdminUserDetailPage /> : <Navigate to="/" replace />} />
              <Route path="/profile" element={token ? <ProfilePage /> : <Navigate to="/" replace />} />
              <Route path="/admin/profile/:userId" element={token && user?.role === 'admin' ? <ProfilePage /> : <Navigate to="/" replace />} />
              <Route path="/settings" element={token ? <SettingsPage /> : <Navigate to="/" replace />} />
              <Route path="/quiz" element={token && (user?.role === 'intern' || user?.role === 'admin') ? <QuizPage /> : <Navigate to="/" replace />} />
              <Route path="/enroll" element={<EnrollmentPage />} />
              <Route path="/enroll/:courseId" element={<EnrollmentPage />} />
              <Route path="/course-access/:token" element={<CourseAccessPage />} />
              <Route path="/admin/curriculum-proposals" element={token && user?.role === 'admin' ? <AdminCurriculumProposalsPage /> : <Navigate to="/" replace />} />
              <Route path="/admin/enrollments" element={token && user?.role === 'admin' ? <AdminEnrollmentsPage /> : <Navigate to="/" replace />} />
              <Route path="/admin/enrollments/:enrollmentId" element={token && user?.role === 'admin' ? <AdminEnrollmentDetailPage /> : <Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </BrowserRouter>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;